import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randBoolean, randNumber, randPhrase } from '@ngneat/falso'

import {
  PauseCancelActionType,
  PauseCancelConditionType,
} from '@/composables/reminderCreationRules.js'
import type { MockServer, MirageServer } from './server.js'
import { createErrorResponse } from './utils.js'

interface ReminderCreationRuleModel {
  name: string
  trigger_in: number
  trigger_type: number
  expiry_days: number
  apply_type: number
  active: boolean
  recurring_type: number | null
  recurring_interval: number | null
  recurring_times: number | null
  conditions: Array<{
    id?: number
    condition: number
    comparison: number
    value: string[]
    value_labels?: Array<{ value: string | number; label: string }>
  }>
  creation_rule_timings: Array<{
    id?: number
    send_method: number
    template: string
    days_to_deadline: number
    is_before: boolean
  }>
  pause_cancel_enabled: boolean
  pause_cancel_conditions: Array<{
    id?: number
    action_type: number
    condition: number
    value: string[]
    value_labels?: Array<{ value: string | number; label: string }>
  }>
  overridden_by_ids?: string[]
  overridden_rule_ids?: string[]
}

export const model = Model as ModelDefinition<ReminderCreationRuleModel>

export const factory = Factory.extend<ReminderCreationRuleModel>({
  name() {
    return randPhrase({ maxCharCount: 30 })
  },
  trigger_in() {
    return randNumber({ min: 0, max: 2 })
  },
  trigger_type() {
    return randNumber({ min: 1, max: 2 })
  },
  expiry_days() {
    return randNumber({ min: 1, max: 365 })
  },
  apply_type() {
    return randNumber({ min: 1, max: 2 })
  },
  active() {
    return randBoolean()
  },
  recurring_type() {
    return null
  },
  recurring_interval() {
    return null
  },
  recurring_times() {
    return null
  },
  conditions() {
    return []
  },
  pause_cancel_enabled() {
    return false
  },
  pause_cancel_conditions() {
    return []
  },
  creation_rule_timings() {
    return [
      {
        id: 1,
        send_method: 0,
        template: '1',
        days_to_deadline: 7,
        is_before: true,
      },
    ]
  },
  overridden_by_ids() {
    return []
  },
  overridden_rule_ids() {
    return []
  },
})

// OPTIONS data formatted for the frontend normalizer
// Frontend expects: response.data.actions.POST.<field>.choices
const optionsData = {
  actions: {
    POST: {
      trigger_in: {
        choices: [
          { value: 0, display_name: 'Consultations and counter sales' },
          { value: 1, display_name: 'Consultations' },
          { value: 2, display_name: 'Counter sales' },
          { value: 3, display_name: 'Patient is created' },
        ],
      },
      trigger_type: {
        choices: [
          { value: 1, display_name: 'Item is added' },
          { value: 2, display_name: 'Invoice is finalized' },
        ],
      },
      apply_type: {
        choices: [
          { value: 1, display_name: 'All of the following conditions are met' },
          { value: 2, display_name: 'Any of the following conditions are met' },
        ],
      },
      recurring_type: {
        choices: [
          { value: 1, display_name: 'Daily' },
          { value: 2, display_name: 'Weekly' },
          { value: 3, display_name: 'Monthly' },
          { value: 4, display_name: 'Yearly' },
        ],
      },
      conditions: {
        child: {
          children: {
            condition: {
              choices: [
                { value: 1, display_name: 'Item' },
                { value: 2, display_name: 'Patient species' },
                { value: 3, display_name: 'Patient age in months' },
                { value: 4, display_name: 'Consultation diagnosis' },
                { value: 5, display_name: 'Amount of consultations' },
              ],
            },
            comparison: {
              choices: [
                { value: 1, display_name: 'In' },
                { value: 2, display_name: 'Not in' },
                { value: 3, display_name: 'Equal to' },
                { value: 4, display_name: 'Not equal to' },
                { value: 5, display_name: 'Greater than' },
                { value: 6, display_name: 'Greater than or equal to' },
                { value: 7, display_name: 'Less than' },
                { value: 8, display_name: 'Less than or equal to' },
                { value: 9, display_name: 'Between' },
              ],
            },
          },
        },
      },
      creation_rule_timings: {
        child: {
          children: {
            send_method: {
              choices: [
                { value: 0, display_name: 'Email' },
                { value: 1, display_name: 'SMS' },
                { value: 2, display_name: 'Email and SMS' },
              ],
            },
          },
        },
      },
      pause_cancel_conditions: {
        child: {
          children: {
            action_type: {
              choices: [
                { value: 1, display_name: 'Pause condition' },
                { value: 2, display_name: 'Pause and cancel condition' },
              ],
            },
            condition: {
              choices: [
                { value: 1, display_name: 'Consultation reason' },
                { value: 2, display_name: 'Consultation reason group' },
                { value: 3, display_name: 'Item' },
              ],
            },
          },
        },
      },
    },
  },
}

export function routes(server: MockServer) {
  server.get(
    '/reminder_creation_rules',
    function (this: MirageServer, schema, request) {
      const rawSearch = request.queryParams['filter[search]']
      const search = (
        Array.isArray(rawSearch) ? rawSearch[0] : rawSearch
      )?.toLowerCase()
      const response = this.serialize(schema.all('reminderCreationRule'))

      if (search) {
        response.data = (
          response.data as Array<{ attributes?: { name?: string } }>
        ).filter((rule) =>
          rule.attributes?.name?.toLowerCase().includes(search),
        )
      }

      return {
        ...response,
        meta: {
          pagination: {
            page: 1,
            pages: 1,
            count: response.data.length,
          },
        },
      }
    },
  )

  server.get(
    '/reminder_creation_rules/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const rule = schema.find('reminderCreationRule', id)

      if (!rule) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Creation rule with id ${id} not found` }],
        })
      }

      const overriddenByIds = rule.attrs.overridden_by_ids || []
      const overriddenByData = overriddenByIds.map((ruleId: string) => ({
        type: 'CreationRule',
        id: ruleId,
      }))

      const overriddenRuleIds = rule.attrs.overridden_rule_ids || []
      const overriddenRulesData = overriddenRuleIds.map((ruleId: string) => ({
        type: 'CreationRule',
        id: ruleId,
      }))

      // Build included array so the frontend can resolve related rule names
      const relatedIds = [...overriddenByIds, ...overriddenRuleIds]
      const included = relatedIds
        .map((relatedId: string) => {
          const relatedRule = schema.find('reminderCreationRule', relatedId)
          if (!relatedRule) {
            return null
          }
          return {
            type: 'CreationRule',
            id: relatedRule.id,
            attributes: { name: relatedRule.attrs.name },
          }
        })
        .filter(Boolean)

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: overriddenByData,
              meta: { count: overriddenByData.length },
            },
            overridden_rules: {
              data: overriddenRulesData,
              meta: { count: overriddenRulesData.length },
            },
          },
        },
        included,
      }
    },
  )

  server.options('/reminder_creation_rules', function () {
    return {
      data: optionsData,
    }
  })

  server.patch(
    '/reminder_creation_rules/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const attrs = JSON.parse(request.requestBody)
      const rule = schema.find('reminderCreationRule', id)

      if (!rule) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Creation rule with id ${id} not found` }],
        })
      }

      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      const previousOverriddenRuleIds: string[] =
        rule.attrs.overridden_rule_ids ?? []

      rule.update(attrs.data.attributes)

      // Handle override relationship updates if relationships are provided
      const newOverriddenRuleIds: string[] | undefined =
        attrs.data.relationships?.overridden_rules?.data?.map(
          (item: { id: string }) => item.id,
        )

      if (newOverriddenRuleIds !== undefined) {
        rule.update({ overridden_rule_ids: newOverriddenRuleIds })

        // Remove this rule from rules that are no longer overridden
        for (const prevId of previousOverriddenRuleIds) {
          if (!newOverriddenRuleIds.includes(prevId)) {
            const prevRule = schema.find('reminderCreationRule', prevId)
            if (prevRule) {
              prevRule.update({
                overridden_by_ids: (
                  prevRule.attrs.overridden_by_ids ?? []
                ).filter((byId: string) => byId !== id),
              })
            }
          }
        }

        // Add this rule to newly overridden rules' overridden_by_ids
        for (const newId of newOverriddenRuleIds) {
          if (!previousOverriddenRuleIds.includes(newId)) {
            const newRule = schema.find('reminderCreationRule', newId)
            if (newRule) {
              newRule.update({
                overridden_by_ids: [
                  ...(newRule.attrs.overridden_by_ids ?? []),
                  id,
                ],
              })
            }
          }
        }
      }

      const finalOverriddenRuleIds = rule.attrs.overridden_rule_ids ?? []
      const overriddenByIds = rule.attrs.overridden_by_ids ?? []

      const overriddenByData = overriddenByIds.map((ruleId: string) => ({
        type: 'CreationRule',
        id: ruleId,
      }))
      const overriddenRulesData = finalOverriddenRuleIds.map(
        (ruleId: string) => ({ type: 'CreationRule', id: ruleId }),
      )

      const relatedIds = [...overriddenByIds, ...finalOverriddenRuleIds]
      const included = relatedIds
        .map((relatedId: string) => {
          const relatedRule = schema.find('reminderCreationRule', relatedId)
          if (!relatedRule) {
            return null
          }
          return {
            type: 'CreationRule',
            id: relatedRule.id,
            attributes: { name: relatedRule.attrs.name },
          }
        })
        .filter(Boolean)

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: overriddenByData,
              meta: { count: overriddenByData.length },
            },
            overridden_rules: {
              data: overriddenRulesData,
              meta: { count: overriddenRulesData.length },
            },
          },
        },
        included,
      }
    },
  )

  server.post(
    '/reminder_creation_rules',
    function (this: MirageServer, schema, request) {
      const attrs = JSON.parse(request.requestBody)

      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      const rule = schema.create('reminderCreationRule', attrs.data.attributes)

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: [],
              meta: { count: 0 },
            },
            overridden_rules: {
              data: [],
              meta: { count: 0 },
            },
          },
        },
      }
    },
  )

  server.delete(
    '/reminder_creation_rules/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const rule = schema.find('reminderCreationRule', id)

      if (!rule) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Creation rule with id ${id} not found` }],
        })
      }

      const force = request.queryParams['force'] === 'true'
      const reminderCount = (rule.attrs.mock_reminder_count as number) ?? 0

      if (!force && reminderCount > 0) {
        return createErrorResponse({
          status: 400,
          errors: [
            {
              detail: `This creation rule has ${reminderCount} associated reminder(s).`,
            },
          ],
        })
      }

      // Clean up dangling references in other rules before destroying
      schema.all('reminderCreationRule').models.forEach((otherRule) => {
        if (otherRule.id === id) {
          return
        }
        const updatedOverriddenRuleIds = (
          (otherRule.attrs.overridden_rule_ids as string[]) ?? []
        ).filter((rid) => rid !== id)
        const updatedOverriddenByIds = (
          (otherRule.attrs.overridden_by_ids as string[]) ?? []
        ).filter((rid) => rid !== id)
        otherRule.update({
          overridden_rule_ids: updatedOverriddenRuleIds,
          overridden_by_ids: updatedOverriddenByIds,
        })
      })

      rule.destroy()
      return new Response(204, {}, '')
    },
  )
}

export function seeds(server: MockServer) {
  server.create('reminderCreationRule', {
    name: 'Annual Health Check',
    trigger_in: 1,
    trigger_type: 1,
    expiry_days: 30,
    apply_type: 1,
    active: true,
    conditions: [],
    creation_rule_timings: [
      {
        id: 1,
        send_method: 0,
        template: '1',
        days_to_deadline: 7,
        is_before: true,
      },
    ],
  })

  server.create('reminderCreationRule', {
    name: 'Vaccination Reminder',
    mock_reminder_count: 42,
    trigger_in: 1,
    trigger_type: 1,
    expiry_days: 14,
    apply_type: 1,
    active: true,
    conditions: [],
    creation_rule_timings: [
      {
        id: 2,
        send_method: 1,
        template: '2',
        days_to_deadline: 3,
        is_before: true,
      },
    ],
    pause_cancel_enabled: true,
    pause_cancel_conditions: [
      {
        id: 1,
        action_type: PauseCancelActionType.Pause,
        condition: PauseCancelConditionType.Item,
        value: ['1'],
        value_labels: [{ value: 1, label: 'Vaccination item' }],
      },
    ],
  })
}
